import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { submitContactForm } from '../../api/publicApi';

const ContactAgentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const property = location.state?.property;

  // ✅ Validate property exists - redirect if missing
  React.useEffect(() => {
    if (!property) {
      toast.error('Please select a property first to make an enquiry.');
      navigate('/property-view-listing');
    }
  }, [property, navigate]);
  // Clean property ID - extract numeric part
  const getCleanPropertyId = (propId) => {
    if (!propId) return null;
    const strId = String(propId);
    if (strId.startsWith('rea-')) {
      return parseInt(strId.replace('rea-', ''), 10);
    }
    if (strId.startsWith('crm-')) {
      return parseInt(strId.replace('crm-', ''), 10);
    }
    return parseInt(strId, 10);
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState([1]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    contact: '',
    property_id: getCleanPropertyId(property?.id),
    property_type: property?.property_type || '',
    bedrooms: property?.beds || null,
    bathrooms: property?.baths || null,
    parking: property?.parkings || null,
    budget_min: null,
    budget_max: null,
    suburbs: [],
    financing_status: '',
    timeline: '',
    notes: '',
    helpWith: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const isValidPhone = (phone) => {
    const cleaned = String(phone).replace(/[^0-9]/g, "");
    return cleaned.length >= 7;
  };

  const helpOptions = [
    'Scheduling an inspection',
    'Rates and fees',
    'Price information',
  ];

  const financingOptions = [
    { value: 'pre-approved', label: 'Pre-approved for finance' },
    { value: 'not-approved', label: 'Not yet approved' },
    { value: 'cash-buyer', label: 'Cash buyer' },
    { value: 'undecided', label: 'Undecided' }
  ];

  const timelineOptions = [
    'Immediately',
    'Within 1 month',
    'Within 3 months',
    'Within 6 months',
    'Within 1 year',
    'Just exploring'
  ];

  const propertyTypes = [
    'House',
    'Apartment',
    'Townhouse',
    'Unit',
    'Villa',
    'Land',
    'Commercial'
  ];

  const handleHelpWithChange = (option) => {
    setFormData(prev => ({
      ...prev,
      helpWith: prev.helpWith.includes(option)
        ? prev.helpWith.filter(item => item !== option)
        : [...prev.helpWith, option]
    }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.full_name.trim()) {
        toast.error('Please enter your full name');
        return;
      }
      if (!formData.email.trim()) {
        toast.error('Please enter your email');
        return;
      }
      if (!isValidEmail(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (!formData.contact.trim()) {
        toast.error('Please enter your contact number');
        return;
      }
      if (!isValidPhone(formData.contact)) {
        toast.error('Please enter a valid contact number');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.notes.trim()) {
        toast.error('Please enter your question or inquiry');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.budget_min && !formData.budget_max) {
        toast.error('Please enter at least one budget amount (min or max)');
        return;
      }
      if ((formData.budget_min ?? 0) < 0 || (formData.budget_max ?? 0) < 0) {
        toast.error('Budget values cannot be negative');
        return;
      }
      if (formData.budget_min != null && formData.budget_max != null) {
        if (formData.budget_min > formData.budget_max) {
          toast.error('Minimum budget cannot be greater than maximum budget');
          return;
        }
      }
    }
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    if (!visitedSteps.includes(nextStep)) {
      setVisitedSteps([...visitedSteps, nextStep]);
    }
  };

  const handleStepClick = (step) => {
    if (visitedSteps.includes(step)) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!isValidPhone(formData.contact)) {
      toast.error('Please enter a valid contact number');
      return;
    }
    if ((formData.budget_min ?? 0) < 0 || (formData.budget_max ?? 0) < 0) {
      toast.error('Budget values cannot be negative');
      return;
    }
    if (formData.budget_min != null && formData.budget_max != null) {
      if (formData.budget_min > formData.budget_max) {
        toast.error('Minimum budget cannot be greater than maximum budget');
        return;
      }
    }
    if (!formData.financing_status) {
      toast.error('Please select your financing status');
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean property_id - extract numeric part
      let cleanPropertyId = formData.property_id;
      if (cleanPropertyId) {
        const strId = String(cleanPropertyId);
        if (strId.startsWith('rea-')) {
          cleanPropertyId = parseInt(strId.replace('rea-', ''), 10);
        } else if (strId.startsWith('crm-')) {
          cleanPropertyId = parseInt(strId.replace('crm-', ''), 10);
        } else {
          cleanPropertyId = parseInt(strId, 10);
        }
      } else {
        cleanPropertyId = null;
      }

      const submitData = {
        flow: 'buyer',
        full_name: formData.full_name,
        email: formData.email,
        contact: formData.contact,
        property_id: cleanPropertyId,
        budget_min: formData.budget_min,
        budget_max: formData.budget_max,
        suburbs: formData.suburbs,
        property_type: formData.property_type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        parking: formData.parking,
        financing_status: formData.financing_status,
        timeline: formData.timeline,
        notes: formData.notes
      };
      const response = await submitContactForm(submitData);

      console.log('Form submitted successfully:', response.data);

      setShowChoiceModal(true);
      toast.success('Your inquiry has been submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to submit inquiry. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleVoiceCallerChoice = () => {
    setShowChoiceModal(false);
    // Show success message for voice caller
    toast.success("AI voice agent will contact you shortly!");
    // Navigate back or to a success page
    navigate("/property-view-listing");
  };

  const handleChatChoice = () => {
    setShowChoiceModal(false);
    // Navigate to chatbot with property questions
    navigate("/chatbot", {
      state: {
        inquiryData: formData,
        initialMessage: "Hi! I'm here to help you with your property search. Let me ask you some questions to better understand your needs."
      }
    });
  };

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Personal Information
        </h2>
        <p className="text-gray-600 text-lg">
          Please provide your contact details so we can get back to you.
        </p>
      </div>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.full_name}
            onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
            placeholder="John Smith"
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="john.smith@example.com"
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.contact}
            onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
            placeholder="+61 412 345 678"
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Your Inquiry
        </h2>
        <p className="text-gray-600 text-lg">
          Tell us what you'd like to know about this property or your requirements.
        </p>
      </div>
      {property && (
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-blue-100">
          <h3 className="font-bold text-gray-900 mb-4 text-lg">Property Details</h3>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Type:</span>
                <span className="font-semibold text-gray-900">{property.property_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Status:</span>
                <span className="font-semibold text-gray-900">{property.status || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Bedrooms:</span>
                <span className="font-semibold text-gray-900">{property.beds || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Bathrooms:</span>
                <span className="font-semibold text-gray-900">{property.baths || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Parking:</span>
                <span className="font-semibold text-gray-900">{property.parkings || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 font-medium">Area:</span>
                <span className="font-semibold text-gray-900">{property.area ? `${property.area} sqm` : 'N/A'}</span>
              </div>
            </div>
            <div className="pt-3 border-t-2 border-blue-200">
              <span className="text-gray-600 font-medium">Address:</span>
              <span className="ml-2 font-semibold text-gray-900">{property.address || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Price:</span>
              <span className="ml-2 font-bold text-[#55C5D0] text-lg">
                {property.price ? `$${Number(property.price).toLocaleString()}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          What would you like to ask or know about? <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Ask about the property, schedule a viewing, or tell us about your requirements..."
          className="w-full h-40 px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 resize-none text-gray-900 placeholder:text-gray-400"
        />
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          What would you like help with?
        </h3>
        <div className="space-y-3">
          {helpOptions.map((option) => (
            <label key={option} className="flex items-center space-x-3 cursor-pointer p-4 rounded-xl border-2 border-gray-200 hover:border-[#55C5D0] hover:bg-blue-50 transition-all duration-200">
              <input
                type="checkbox"
                checked={formData.helpWith.includes(option)}
                onChange={() => handleHelpWithChange(option)}
                className="w-5 h-5 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
              />
              <span className="text-gray-700 font-medium">{option}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Budget Information
        </h2>
        <p className="text-gray-600 text-lg">
          Help us understand your budget range to provide better recommendations.
        </p>
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Minimum Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={formData.budget_min || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  budget_min: e.target.value ? Math.max(0, parseInt(e.target.value)) : null
                }))}
                placeholder="500,000"
                className="w-full pl-8 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Maximum Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="number"
                value={formData.budget_max || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  budget_max: e.target.value ? Math.max(0, parseInt(e.target.value)) : null
                }))}
                placeholder="750,000"
                className="w-full pl-8 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Preferred Suburbs <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="text"
            value={formData.suburbs.join(', ')}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              suburbs: e.target.value.split(',').map(s => s.trim()).filter(s => s)
            }))}
            placeholder="Melbourne, Carlton, Fitzroy"
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 placeholder:text-gray-400"
          />
          <p className="mt-2 text-sm text-gray-500">Separate multiple suburbs with commas</p>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Financing & Timeline
        </h2>
        <p className="text-gray-600 text-lg">
          Tell us about your financing status and when you're looking to buy.
        </p>
      </div>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-4">
            Financing Status <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {financingOptions.map((option) => (
              <label key={option.value} className={`flex items-center space-x-4 cursor-pointer p-4 border-2 rounded-xl transition-all duration-200 ${formData.financing_status === option.value
                ? 'border-[#55C5D0] bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}>
                <input
                  type="radio"
                  name="financing_status"
                  value={option.value}
                  checked={formData.financing_status === option.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, financing_status: e.target.value }))}
                  className="w-5 h-5 text-[#55C5D0] border-gray-300 focus:ring-[#55C5D0]"
                />
                <span className={`font-medium ${formData.financing_status === option.value ? 'text-[#55C5D0]' : 'text-gray-700'
                  }`}>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Timeline <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <select
            value={formData.timeline}
            onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/50 focus:border-[#55C5D0] transition-all duration-200 text-gray-900 bg-white"
          >
            <option value="">Select your timeline</option>
            {timelineOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="text-center space-y-8 py-6">
      <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-green-200/50 animate-bounce">
        <Check className="w-12 h-12 text-green-600" strokeWidth={3} />
      </div>

      <div>
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Inquiry Submitted Successfully!
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Thank you for your interest. Our dedicated agent will review your inquiry and get back to you within 24 hours.
        </p>
      </div>
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 text-left border-2 border-blue-100 max-w-2xl mx-auto">
        <h3 className="font-bold text-gray-900 mb-6 text-xl flex items-center">
          <span className="w-8 h-8 bg-[#55C5D0] rounded-full flex items-center justify-center mr-3">
            <ChevronRight className="w-5 h-5 text-white" />
          </span>
          What happens next?
        </h3>
        <ul className="space-y-4">
          <li className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Inquiry Review</p>
              <p className="text-sm text-gray-600">We'll carefully review your inquiry and property preferences</p>
            </div>
          </li>
          <li className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Agent Contact</p>
              <p className="text-sm text-gray-600">Our experienced agent will reach out to you within 24 hours</p>
            </div>
          </li>
          <li className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Property Details</p>
              <p className="text-sm text-gray-600">We'll provide comprehensive information and schedule property viewings</p>
            </div>
          </li>
        </ul>
      </div>
      <button
        onClick={() => navigate('/property-view-listing')}
        className="w-full max-w-md mx-auto bg-gradient-to-r from-[#55C5D0] to-[#48b5c0] text-white py-4 px-8 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center shadow-lg shadow-[#55C5D0]/30"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Properties
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-10">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Collings Contact Form</h1>
            <p className="text-base text-gray-600">
              Complete the form below and we'll get back to you within 24 hours
            </p>
            {currentStep !== 5 && (
              <p className="text-sm text-gray-500 mt-2">
                Step {currentStep} of 4
              </p>
            )}
          </div>
        </div>
        {currentStep !== 5 && (
          <div className="mb-10 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step, index) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => handleStepClick(step)}
                      disabled={!visitedSteps.includes(step)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${currentStep >= step
                        ? 'bg-gradient-to-br from-[#55C5D0] to-[#48b5c0] text-white shadow-lg shadow-[#55C5D0]/30'
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                        } ${visitedSteps.includes(step)
                          ? 'cursor-pointer hover:scale-110 hover:shadow-xl'
                          : 'cursor-not-allowed opacity-60'
                        }`}
                    >
                      {currentStep > step ? <Check className="w-5 h-5" /> : step}
                    </button>
                    <span className={`text-xs font-medium mt-2 ${currentStep >= step ? 'text-[#55C5D0]' : 'text-gray-400'
                      }`}>
                      {step === 1 ? 'Personal' : step === 2 ? 'Inquiry' : step === 3 ? 'Budget' : 'Financing'}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className="flex-1 h-1 mx-3 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${currentStep > step ? 'bg-gradient-to-r from-[#55C5D0] to-[#48b5c0]' : 'bg-gray-200'
                          }`}
                        style={{ width: currentStep > step ? '100%' : '0%' }}
                      ></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 lg:p-10">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
          {currentStep !== 5 && (
            <div className="flex justify-between mt-10 pt-6 border-t border-gray-100">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center px-6 py-3.5 text-sm font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
              ) : (
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center px-6 py-3.5 text-sm font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Cancel
                </button>
              )}
              {currentStep < 4 ? (
                <button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-[#55C5D0] to-[#48b5c0] text-white px-8 py-3.5 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center space-x-2 shadow-lg shadow-[#55C5D0]/30"
                >
                  <span>Continue</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-[#55C5D0] to-[#48b5c0] text-white px-8 py-3.5 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center space-x-2 shadow-lg shadow-[#55C5D0]/30"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Inquiry</span>
                      <Check className="w-5 h-5" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Choice Modal */}
      {showChoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                How would you like to proceed?
              </h3>
              <p className="text-gray-600">
                We'd like to ask you a few more questions to better assist you with your property search.
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleVoiceCallerChoice}
                className="w-full bg-gradient-to-r from-[#55C5D0] to-[#48b5c0] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-3"
              >
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">AI Voice Caller</div>
                  <div className="text-sm opacity-90">Get a personalized call from our AI agent</div>
                </div>
              </button>

              <button
                onClick={handleChatChoice}
                className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-3"
              >
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Chat Assistant</div>
                  <div className="text-sm opacity-90">Chat with our AI assistant online</div>
                </div>
              </button>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowChoiceModal(false)}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactAgentForm;
